import {
    chainWalk, getAssetLabelFromMac,
    getLocationFromAssetMappings,
    getNodeId,
    getUTRRegion,
    isResponseCode5xx,
    logOperationalMetric,
    sortAlphaNumeric,
} from "../../../util/Util";
import {
    ACTIVE,
    ALERT_ADD_ASSET_MESSAGE,
    ALERT_UPDATE_ASSET_MESSAGE,
    ALERT_UPDATE_SUCCESS_ASSET_MESSAGE, INACTIVE,
    LIDAR_ASSET_TYPE,
    LOADING_FAILED
} from "../../../util/Constants";
import {
    addAssetV2,
    batchUpdateAssetV2,
    getAssetByAssetLabel,
    getAssets,
    getAssetsConnectivity,
    setAssetStatus, updateAsset
} from "../../../resources/AssetResource";
import {getCartRecipes} from "../../../resources/NodeResource";
import {
    SCANLESS_SUPPORTED_CART_RECIPES_EU,
    SCANLESS_SUPPORTED_CART_RECIPES_JP,
    SCANLESS_SUPPORTED_CART_RECIPES_NA
} from "../../../util/Validations";
import {updateConfigurationsInLidarAssetObject} from "../datamodel/LidarAssetModelResource";

export const metricProcessName = "LIDAR_DEVICE_DETAILS";

export async function fetchLidarDeviceDetails(dispatcher) {
    const request = {
        nodeId: getNodeId(),
        assetType: LIDAR_ASSET_TYPE
    }
    await getLidarDeviceDetails(dispatcher, request)
}

async function getLidarDeviceDetails(dispatcher, request) {
    const startTime = Date.now();
    const deviceIdentifierList = [];
    let assetsList = [];
    let lidarConnectivity = {};
    let pageToken = null;
    let getAssetsResult;

    do {
        getAssetsResult = await getAssets(request.nodeId, request.assetType, pageToken);
        if (getAssetsResult.status === 200) {
            logOperationalMetric(startTime, metricProcessName, "getLidarDeviceDetails", true, false);
            assetsList = assetsList.concat(await getAssetByAssetlabel(getAssetsResult));
            pageToken = getAssetsResult.data.pageToken;
        } else {
            const isFault = isResponseCode5xx(getAssetsResult.status)
            logOperationalMetric(startTime, metricProcessName, "getLidarDeviceDetails", false, isFault);
        }
    } while(getAssetsResult.status === 200 && getAssetsResult.data.pageToken)

    assetsList.forEach(asset => {
        deviceIdentifierList.push(asset.assetDetails.assetAttributes.ControllerIP);
    })

    if (deviceIdentifierList && deviceIdentifierList.length > 0) {
        lidarConnectivity = await getAssetsConnectivity(request.nodeId, request.assetType,
            [...new Set(deviceIdentifierList)]);
    }
    const cartRecipesMap = await getCartRecipeMap();
    const model = await extractLidarDeviceList(assetsList, lidarConnectivity, cartRecipesMap);
    model.cartRecipesMap = cartRecipesMap;
    dispatcher({type: "RECEIVE_DATA", data: model});
}

function getNumberOfLidarSensors(cartType, cartRecipesMap) {
    const cartRecipe = cartType && cartRecipesMap && cartRecipesMap.get(cartType);
    return (cartRecipe && cartRecipe.numberOfLidarSensors) || 0;
}

async function extractLidarDeviceList (assetsList, lidarConnectivity, cartRecipesMap) {
    const model = {
        lidarDeviceInformation: {},
        macAddressArray: []
    }
    const lidarConnectivityMap = {}
    if (lidarConnectivity.data && lidarConnectivity.data.assetConnectivityList) {
        lidarConnectivity.data.assetConnectivityList.forEach(connectivity => {
            lidarConnectivityMap[connectivity.identifier] = connectivity;
        });
    }
    assetsList.forEach((asset) => {
        if ("assetDetails" in asset) {
            const lidarDeviceDetail = asset.assetDetails;
            if (lidarDeviceDetail.assetStatus === ACTIVE || lidarDeviceDetail.assetStatus === INACTIVE) {
                model.macAddressArray.push(lidarDeviceDetail.assetAttributes.mac)
                const detail = lidarConnectivityMap[lidarDeviceDetail.assetAttributes.ControllerIP];
                const lidarLocation = getLocationFromAssetMappings(asset.assetMappings);
                const startEndAisleLabel = getStartEndAisleLabelFromAssetMappings(
                    asset.assetMappings);
                const columnTypes = getColumnTypes(asset.assetMappings[0]);
                const numberOfNonOVLidarSensors = getNumberOfLidarSensors(columnTypes.nonOvColumnType, cartRecipesMap);
                model.lidarDeviceInformation[lidarDeviceDetail.assetLabel.value] = {
                    mac: lidarDeviceDetail.assetLabel.value,
                    ip: lidarDeviceDetail.assetAttributes.ControllerIP,
                    lidarControllerType: lidarDeviceDetail.assetAttributes.ControllerType,
                    status: lidarDeviceDetail.assetStatus,
                    connectivity: detail ? detail.status : LOADING_FAILED,
                    timestamp: detail ? detail.lastUpdateTime : "",
                    location: lidarLocation ? lidarLocation : "Not Configured",
                    startAisleLabel: startEndAisleLabel.startAisleLabel,
                    endAisleLabel: startEndAisleLabel.endAisleLabel,
                    nonOvColumnType: columnTypes.nonOvColumnType,
                    numberOfNonOVLidarSensors: numberOfNonOVLidarSensors,
                    oneTierOvColumnType: columnTypes.oneTierOvColumnType,
                    twoTierOvColumnType: columnTypes.twoTierOvColumnType,
                    aislesCount: asset.assetMappings.length,
                    creationTime: lidarDeviceDetail.creationTime,
                    obstructionDisabledUntil: Number(chainWalk(() => lidarDeviceDetail.assetAttributes.obstructionDisabledUntil,
                        0)),
                    displayableAssetMappings: prepareDisplayableAssetMappingData(asset.assetMappings),
                }
            }
        }
    });
    return model;
}

async function getAssetByAssetlabel(getAssetsResult) {
    const promises = [];
    const startTime = Date.now();
    if(getAssetsResult.data && getAssetsResult.data.assetDetailsList) {
        getAssetsResult.data.assetDetailsList.forEach((lidarDeviceDetail) => {
            if (lidarDeviceDetail.assetLabel) {
                promises.push(getAssetByAssetLabel(lidarDeviceDetail.assetLabel))
            }
        });
    }
    const getAssetByAssetLabelResultsList = await Promise.all(promises);
    const assetList = [];
    getAssetByAssetLabelResultsList.forEach((res) => {
        if (res.status === 200) {
            logOperationalMetric(startTime, metricProcessName, "getAssetByAssetlabel", true, false);
            assetList.push(res.data.asset);
        } else {
            const isFault = isResponseCode5xx(res.status)
            logOperationalMetric(startTime, metricProcessName, "getAssetByAssetlabel", false, isFault);
        }
    });
    return assetList;
}

async function getCartRecipeMap() {
    let eligibleCartRecipes = [];
    if (getUTRRegion() === "NA") {
        eligibleCartRecipes = SCANLESS_SUPPORTED_CART_RECIPES_NA.NON_OV
            .concat(SCANLESS_SUPPORTED_CART_RECIPES_NA.TWO_TIER_OV)
            .concat(SCANLESS_SUPPORTED_CART_RECIPES_NA.ONE_TIER_OV)
            .concat(SCANLESS_SUPPORTED_CART_RECIPES_NA.FLEX_RACK);
    } else if (getUTRRegion() === "EU") {
        eligibleCartRecipes = SCANLESS_SUPPORTED_CART_RECIPES_EU.NON_OV
            .concat(SCANLESS_SUPPORTED_CART_RECIPES_EU.TWO_TIER_OV)
            .concat(SCANLESS_SUPPORTED_CART_RECIPES_EU.ONE_TIER_OV)
            .concat(SCANLESS_SUPPORTED_CART_RECIPES_EU.FLEX_RACK);
    } else if (getUTRRegion() === "FE") {
        eligibleCartRecipes = SCANLESS_SUPPORTED_CART_RECIPES_JP.NON_OV
            .concat(SCANLESS_SUPPORTED_CART_RECIPES_JP.TWO_TIER_OV)
            .concat(SCANLESS_SUPPORTED_CART_RECIPES_JP.ONE_TIER_OV)
            .concat(SCANLESS_SUPPORTED_CART_RECIPES_JP.FLEX_RACK);
    }
    return await getCartRecipes(eligibleCartRecipes, metricProcessName);
}

function getColumnTypes(assetMapping) {
    const columnTypes = {};
    if (assetMapping && assetMapping.mappingAttributes && assetMapping.mappingAttributes.mappedColumns) {
        try {
            const mappedColumns = JSON.parse(assetMapping.mappingAttributes.mappedColumns);
            if (Array.isArray(mappedColumns)) {
                mappedColumns.forEach((mappedColumn) => {
                    if (!mappedColumn.rowLabels) {
                        columnTypes.nonOvColumnType = mappedColumn.cartType;
                    } else if (mappedColumn.rowLabels && Array.isArray(mappedColumn.rowLabels) && mappedColumn.rowLabels.length) {
                        if (mappedColumn.rowLabels.length === 1) {
                            columnTypes.oneTierOvColumnType = mappedColumn.cartType;
                        } else if (mappedColumn.rowLabels.length === 2) {
                            columnTypes.twoTierOvColumnType = mappedColumn.cartType;
                        }
                    }
                });
            }
        } catch (error) {
            console.error("Error parsing mappedColumns JSON:", error);
        }
    }
    return columnTypes;
}

function prepareDisplayableAssetMappingData(assetMappings) {
    const displayableAssetMappingList = [];
    assetMappings.forEach((assetMapping, index) => {
        try {
            const mappedColumns = JSON.parse(assetMapping.mappingAttributes.mappedColumns);
            displayableAssetMappingList[index] = {
                "aisleLabel": assetMapping.mappedResourceLabel,
                "columns": mappedColumns
            };
        } catch (error) {
            console.error("Error parsing mappedColumns JSON in prepareDisplayableAssetMappingData:", error);
            // Add a placeholder entry with empty columns to avoid breaking the UI
            displayableAssetMappingList[index] = {
                "aisleLabel": assetMapping.mappedResourceLabel,
                "columns": []
            };
        }
    })

    displayableAssetMappingList.sort(function(a,b) {
       return a.aisleLabel.localeCompare(b.aisleLabel, undefined, {
           numeric: true,
           sensitivity: 'base'
       })
    });
    return displayableAssetMappingList;
}

export function getStartEndAisleLabelFromAssetMappings(assetMappings) {
    let aisleLabels = [];
    assetMappings.forEach((mapping) => {
        if(mapping.mappedResourceLabel) {
            aisleLabels.push(mapping.mappedResourceLabel);
        }
    });
    aisleLabels = sortAlphaNumeric(aisleLabels);
    return {"startAisleLabel" : aisleLabels[0], "endAisleLabel": aisleLabels[aisleLabels.length-1]};
}

export async function addLidarController(dispatcher, state, showCustomAlert) {
    const requestBody = {
        lidarControllerConfig: state.lidarControllerConfig,
        nodeId: getNodeId(),
        assetType: LIDAR_ASSET_TYPE
    }
    await callAddAssetV2(dispatcher, requestBody, showCustomAlert);
}

async function callAddAssetV2(dispatcher, requestBody, showCustomAlert) {
    const startTime = Date.now();
    const response = await addAssetV2(requestBody);
    if (response.status === 200) {
        logOperationalMetric(startTime, metricProcessName, "AddAssetV2", true, false);
        showCustomAlert(ALERT_UPDATE_SUCCESS_ASSET_MESSAGE);
        fetchLidarDeviceDetails(dispatcher);
    } else {
        const isFault = isResponseCode5xx(response.status)
        logOperationalMetric(startTime, metricProcessName, "AddAssetV2", false, isFault);
        showCustomAlert(chainWalk(() => JSON.parse(response.errorResponse.errorMessage).message, ALERT_ADD_ASSET_MESSAGE));
    }
}

export async function updateLidarController(dispatcher, state, showCustomAlert) {
    const requestBody = {
        lidarControllersConfigList: [state.lidarControllerConfig],
        nodeId: getNodeId(),
        assetType: LIDAR_ASSET_TYPE
    }
    await callBatchUpdateAssetV2(dispatcher, requestBody, showCustomAlert);
}

async function callBatchUpdateAssetV2(dispatcher, requestBody, showCustomAlert) {
    const startTime = Date.now();
    const response = await batchUpdateAssetV2(requestBody);
    if (response.status === 200) {
        logOperationalMetric(startTime, metricProcessName, "BatchUpdateAssetV2", true, false);
        showCustomAlert(ALERT_UPDATE_SUCCESS_ASSET_MESSAGE);
        fetchLidarDeviceDetails(dispatcher);
    } else {
        const isFault = isResponseCode5xx(response.status)
        logOperationalMetric(startTime, metricProcessName, "BatchUpdateAssetV2", false, isFault);
        showCustomAlert(chainWalk(() => JSON.parse(response.errorResponse.errorMessage).message, ALERT_UPDATE_ASSET_MESSAGE));
    }
}

export async function updateLidarControllerStatus(dispatcher, mac, status, showCustomAlert) {
    const assetLabel = {
            type: "MAC_ADDRESS",
            value: mac
    };
    await callSetAssetStatus(dispatcher, assetLabel, status, showCustomAlert);
}

async function callSetAssetStatus(dispatcher, assetLabel, status, showCustomAlert) {
    const startTime = Date.now();
    const response = await setAssetStatus(assetLabel, status);
    if (response.status === 200) {
        logOperationalMetric(startTime, metricProcessName, "SetAssetStatus", true, false);
        showCustomAlert(ALERT_UPDATE_SUCCESS_ASSET_MESSAGE);
    } else {
        const isFault = isResponseCode5xx(response.status)
        logOperationalMetric(startTime, metricProcessName, "SetAssetStatus", false, isFault);
        showCustomAlert(chainWalk(() => JSON.parse(response.errorResponse.errorMessage).message, ALERT_UPDATE_ASSET_MESSAGE));
    }
}


export const updateLidarConfiguration = async (dispatcher, configurations, mac, showCustomAlert) => {
    // Get existing configuration
    const getAssetResult = await getAssetByAssetLabel(getAssetLabelFromMac(mac));

    // Merge current configuration into existing configuration and update
    const startTime = Date.now();
    const updatedAsset = updateConfigurationsInLidarAssetObject(getAssetResult.data.asset, configurations);

    const updateResult = await updateAsset(updatedAsset, chainWalk(() => getAssetResult.data.asset.assetDetails.creationTime, null));

    if (updateResult.status === 200) {
        logOperationalMetric(startTime, metricProcessName, "UpdateLidarConfiguration", true, false);
        showCustomAlert(ALERT_UPDATE_SUCCESS_ASSET_MESSAGE);
        fetchLidarDeviceDetails(dispatcher);
    } else {
        const isFault = isResponseCode5xx(updateResult.status)
        logOperationalMetric(startTime, metricProcessName, "UpdateLidarConfiguration", false, isFault);
        showCustomAlert(chainWalk(() => JSON.parse(updateResult.errorResponse.errorMessage).message, ALERT_UPDATE_ASSET_MESSAGE));
    }
}




// WEBPACK FOOTER //
// ./src/components/devicemanagement/lidar/network/LidarOverviewNetworkAPI.js